import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const HundredMileHouse = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const bcNeighbourhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e5%8d%91%e8%af%97%e7%9c%81-%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/'
    : 'https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/'

  const cheapNeighbourhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e7%a7%9f%e6%88%bf%e5%ae%9d%e5%85%b8-%e5%a4%a7%e6%b8%a9%e5%93%a5%e5%8d%8e%e7%a7%9f%e9%87%91%e6%9c%80%e4%be%bf%e5%ae%9c%e7%9a%8410%e4%b8%aa%e5%9c%b0%e5%8c%ba/'
    : 'https://liv.rent/blog/neighbourhoods/cheapest-neighbourhoods-for-rent-in-metro-vancouver/'

  const getAbout = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.100-mile-house:About 100 Mile House Paragraph 1">
            Located in the Cariboo region of{' '}
            <LinkText href="/rental-listings/british-columbia">
              British Columbia (BC)
            </LinkText>{' '}
            , 100 Mile House is a captivating blend of history and modernity.
            Stemming from its role as a rest stop during the Cariboo Gold Rush,
            the town’s name reflects its historical significance. Positioned
            along the Cariboo Highway, it serves as a pivotal link between
            southern and northern BC. The area’s abundance of lakes and the
            nearby Cariboo Mountains offer an outdoor enthusiast’s haven, with
            activities ranging from fishing and boating to hiking and skiing.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.100-mile-house:About 100 Mile House Paragraph 2">
            Beyond its natural charm, 100 Mile House fosters a strong sense of
            community, regularly hosting events that celebrate its heritage.
            This blend of history, recreation, and camaraderie solidifies 100
            Mile House as a distinctive gem within the tapestry of{' '}
            <LinkText href={bcNeighbourhoodsLink}>
              cities and neighbourhoods in BC
            </LinkText>
            .
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={cheapNeighbourhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$900 - $1,800',
          bed2: '$1,500 - $2,500',
          bed3: '$1,800 - $2,800'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.100-mile-house:Apartment Hunting in 100 Mile House Paragraph">
            Compared to cities like{' '}
            <LinkText href="/rental-listings/city/vancouver">
              Vancouver
            </LinkText>{' '}
            and{' '}
            <LinkText href="/rental-listings/city/richmond">Richmond</LinkText>,
            it’s much easier to find{' '}
            <LinkText href={links.under1000}>
              cheap rentals in 100 Mile House
            </LinkText>{' '}
            – although{' '}
            <LinkText href={links.under1000}>
              apartments for rent under $1000
            </LinkText>{' '}
            are still uncommon. To start your search, you can refer to this
            guide on{' '}
            <LinkText href={cheapNeighbourhoodsLink}>
              the cheapest neighbourhoods in Metro Vancouver
            </LinkText>
            .
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.100-mile-house:Apartments for rent 100 Mile House Paragraph">
              In 100 Mile House,{' '}
              <LinkText href={links.apartment}>apartments for rent</LinkText>{' '}
              are not as prevalent. There are only a few apartment complexes in
              the area. Apartments generally require much less maintenance than
              houses, making them ideal for renters who don’t need as much
              space. As a result, they are more popular among singles, couples,
              or small families.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.100-mile-house:Houses for rent 100 Mile House Paragraph">
              Detached houses for rent are quite common in 100 Mile House. They
              are typically suitable for those with a higher budget, for
              families seeking more space or who have multiple roommates. Both
              partial houses and entire{' '}
              <LinkText href={links.house}>houses for rent</LinkText> are
              common, as well as both new builds and older homes. Rental prices
              can range broadly depending on the property’s size, condition, and
              location.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.100-mile-house:Townhouses for rent 100 Mile House Paragraph">
              Townhouses for rent are less common in 100 Mile House. There are
              only a few available, and are generally more suitable for small
              families. Combining the best qualities of both apartments and
              whole houses,{' '}
              <LinkText href={links.townhouse}>townhouses for rent</LinkText>{' '}
              offer an attractive combination of freedom and space, which
              normally includes your own private yard and entrance.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Basements for rent CITY Paragraph">
              For those looking to find affordable rental housing in this
              increasingly expensive city, basements for rent in {{ cityName }}{' '}
              pose an attractive alternative. Since house prices are so
              expensive, homeowners will sometimes rent out their basements as a
              way to offset their mortgage payments. This makes it easy for
              renters to find relatively inexpensive basements for rent in all
              areas of the city.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.100-mile-house:Condos for rent 100 Mile House Paragraph">
              <LinkText href={links.apartment}>Condos for rent</LinkText> are
              also not commonly seen in 100 Mile House. These types of rental
              accommodation are perfect for those who like to be in the centre
              of the action, and don’t need as much space. Generally, they are
              more suitable for couples or singles looking for a low-maintenance
              lifestyle.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Rooms for rent CITY Paragraph">
              Sharing a house or apartment is practically essential for those
              looking for cheap rent.{' '}
              <LinkText href={links.room}>
                Rooms for rent in {{ cityName }}
              </LinkText>{' '}
              are a great way to experience the best of the city on a budget,
              and can offer you the opportunity to live in neighbourhoods that
              would otherwise be unaffordable.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.100-mile-house:Short term rentals 100 Mile House Paragraph">
              100 Mile House, a small town in the South Cariboo region of
              British Columbia, is not primarily known for a high volume of{' '}
              <LinkText href={links.shortTerm}>short-term rentals</LinkText>.
              The local economy is more centered around forestry, agriculture,
              and tourism, with job opportunities reflecting these sectors.
              While there are educational facilities, they are limited compared
              to larger metropolitan areas. The area sees a seasonal influx of
              visitors, often leading to an increased demand for short-term
              accommodation, particularly in the tourism-heavy summer months.
              For those seeking rentals, the appeal often lies in the town’s
              close-knit community and picturesque natural surroundings, rather
              than educational or professional pursuits. When{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                considering short-term rentals
              </LinkText>
              , potential renters often value flexibility, especially those
              exploring the region or on temporary work assignments, making it
              one component in the decision-making process.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals LOCATION Generic Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ cityNameWithState }}
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that in {{ cityName }}, there are neighbourhoods
              where rent is considerably cheaper, so exploring these options is
              a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.100-mile-house:Living in 100 Mile House Paragraph">
            100 Mile House presents a lifestyle steeped in rural tranquility and
            outdoor adventure, ideal for individuals, families, or retirees who
            cherish nature, community spirit, and a slower pace of life. The
            town, known for its pivotal role during the Cariboo Gold Rush, now
            thrives on forestry, ranching, and tourism, offering a
            renter-friendly atmosphere albeit with a smaller, more limited
            housing market. It’s particularly suitable for outdoor enthusiasts,
            with its proximity to pristine lakes, hiking trails, and winter
            sports resources, such as the nearby ski hills and snowmobiling
            trails. 100 Mile House also prides itself on its annual events, like
            the Cariboo Challenge Sled Dog Race and the South Cariboo Garlic
            Festival, fostering a strong sense of community. While it may lack
            the bustle and immediate amenities of a larger city, its charm lies
            in its close-knit feel, historical significance, and abundant
            natural beauty, offering a serene respite from urban life.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e5%8a%a0%e6%8b%bf%e5%a4%a7%e7%95%99%e5%ad%a65%e5%a4%a7%e5%9f%8e%e5%b8%82-%e7%a7%9f%e6%88%bf%e3%80%81%e5%ad%a6%e8%b4%b9%e3%80%81%e5%b7%a5%e4%bd%9c%e3%80%81%e7%ad%be%e8%af%81%e8%af%a6%e8%a7%a3/">
              加拿大最佳移民城市
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e6%b8%a9%e5%93%a5%e5%8d%8e%e3%80%81%e5%a4%9a%e4%bc%a6%e5%a4%9a%e6%af%94%e8%be%83/">
              温哥华 vs 多伦多：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/%E6%B8%A9%E5%93%A5%E5%8D%8E%E5%92%8C%E7%B4%A0%E9%87%8C%E5%B1%85%E4%BD%8F%E6%88%90%E6%9C%AC%E6%AF%94%E8%BE%83/">
              温哥华 vs 素里：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/toronto-zh/2021%e7%89%88%e5%a4%9a%e4%bc%a6%e5%a4%9a%e3%80%81%e8%92%99%e7%89%b9%e5%88%a9%e5%b0%94%e7%94%9f%e6%b4%bb%e6%88%90%e6%9c%ac%e6%af%94%e8%be%83/">
              多伦多 vs 蒙特利尔：生活成本比较
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/2020/11/best-canadian-cities-for-immigration/">
              Best Canadian cities for immigration
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2019/11/vancouver-or-toronto-a-cost-of-living-comparison/">
              Vancouver vs Toronto: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/02/surrey-vs-vancouver-bc-comparison/">
              Vancouver vs Surrey: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/04/toronto-vs-montreal-cost-of-living-comparison/">
              Toronto vs Montreal: Cost of living comparison
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.100-mile-house:Popular Neighbourhoods 100 Mile House Paragraph">
          Like any city, 100 Mile House is made up of smaller neighbourhoods
          with their own distinct features. Some of the most popular
          neighbourhoods include Downtown Area, Walker Valley, Ski Hill Area, 99
          Mile Hill, Bridge Lake, Lac la Hache, Watch Lake, and Forest Grove.
          For more info about 100 Mile House, you can consult this guide on{' '}
          <LinkText href={bcNeighbourhoodsLink}>BC neighbourhoods</LinkText>.
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.100-mile-house:Local Landmarks 100 Mile House Paragraph">
          In 100 Mile House, a town steeped in the rich history of British
          Columbia’s Cariboo region, several landmarks capture its cultural
          heritage and natural beauty. The most iconic is the &quot;World’s
          Largest Cross-Country Skis,&quot; standing proudly as a testament to
          the area’s enthusiasm for winter sports. The 108 Mile Ranch Heritage
          Site is another must-visit, offering a glimpse into the region’s past,
          with its collection of historic buildings and artifacts from the
          pioneering days. The Centennial Park, with its lovely waterfalls and
          walking trails, provides a peaceful retreat right in the heart of the
          town, often used for community gatherings and events. Lastly, the
          nearby Bridge Creek Falls, a natural beauty spot, epitomizes the
          area’s outdoor spirit, inviting residents and visitors alike to
          explore the scenic landscape that defines 100 Mile House.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default HundredMileHouse
